import PhotoSlot from '../components/PhotoSlots';
import { publicHref } from '../lib/publicUrls';
import { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Award, ChevronDown, Dumbbell, Heart, Users, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import { TourClosing } from '../components/TourJourney';
import { COACHES, COACHES_BY_SLUG } from '../data/coaches';
import './OurCoachesPage.css';

const FILTER_CHIPS = ['All', 'MX4', 'Personal Training', 'Pilates'];
const CoachAvatar = ({ coach }) => <PhotoSlot id={`coach-${coach.slug}`} label={`${coach.name} portrait`} className="dv-coaches__portrait" />;

const CoachCard = ({ coach, onOpen }) => (
  <article className="dv-coaches__profile" aria-labelledby={`coach-name-${coach.slug}`}>
    <div className="dv-coaches__profile-image"><CoachAvatar coach={coach} /></div>
    <div className="dv-coaches__profile-heading">
      <h3 id={`coach-name-${coach.slug}`} className="dv-size-subheading text-2xl font-black">{coach.name}</h3>
      <p className="text-xs font-medium leading-snug">{coach.role}</p>
      <div className="dv-coaches__specialties">
        {coach.classes.slice(0, 3).map(c => <Badge key={c} variant="secondary" className="dv-coaches__badge text-[10px] font-semibold">{c}</Badge>)}
        {coach.classes.length > 3 && <Badge variant="secondary" className="dv-coaches__badge text-[10px]">+{coach.classes.length - 3}</Badge>}
      </div>
    </div>
    <p className="dv-coaches__excerpt dv-size-body text-xs line-clamp-3">{coach.philosophy}</p>
    <button type="button" className="dv-coaches__bio-link text-xs font-bold" aria-label={`Read full bio for ${coach.name}`} aria-haspopup="dialog" data-testid={`coach-card-${coach.slug}`} onClick={event => onOpen(coach, event.currentTarget)}>
      Read full bio <ChevronDown size={12} className="rotate-[-90deg]" />
    </button>
  </article>
);

const CoachModal = ({ coach, onClose, restoreFocus }) => (
  <DialogPrimitive.Root open={!!coach} onOpenChange={open => { if (!open) onClose(); }}>
    {coach && <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="dv-coaches-dialog__overlay" />
      <DialogPrimitive.Content className="dv-coaches-dialog" data-testid="coach-modal" onCloseAutoFocus={restoreFocus}>
        <div className="dv-coaches-dialog__toolbar">
          <span className="dv-coaches-dialog__label">Full biography</span>
          <DialogPrimitive.Close className="dv-coaches-dialog__close text-lg font-bold" aria-label="Close" data-testid="coach-modal-close">×</DialogPrimitive.Close>
        </div>
        <div className="dv-coaches-dialog__scroll">
          <div className="dv-coaches-dialog__layout">
            <figure className="dv-coaches-dialog__image">
              <CoachAvatar coach={coach} large />
            </figure>
            <div className="dv-coaches-dialog__copy">
              <DialogPrimitive.Title asChild><h2 className="dv-size-subheading text-2xl md:text-3xl font-black mb-1">{coach.name}</h2></DialogPrimitive.Title>
              <DialogPrimitive.Description asChild><p className="text-sm font-semibold mb-4">{coach.role}</p></DialogPrimitive.Description>
              <div className="mb-5">
                <p className="text-[11px] font-bold tracking-wider uppercase mb-2"><Dumbbell size={12} className="inline mr-1 -mt-0.5" /> Teaches</p>
                <div className="dv-coaches__specialties">{coach.classes.map(c => <Badge key={c} className="dv-coaches__badge font-semibold">{c}</Badge>)}</div>
              </div>
              <div className="mb-5">
                <p className="text-[11px] font-bold tracking-wider uppercase mb-2"><Heart size={12} className="inline mr-1 -mt-0.5" /> Philosophy</p>
                <p className="dv-size-body text-sm leading-relaxed whitespace-pre-line">{coach.philosophy}</p>
              </div>
              <div>
                <p className="text-[11px] font-bold tracking-wider uppercase mb-2"><Award size={12} className="inline mr-1 -mt-0.5" /> Certifications</p>
                <ul className="text-xs space-y-1">{coach.certifications.map((c, i) => <li key={i} className="dv-size-body flex items-start gap-1.5"><span className="mt-0.5">▸</span>{c}</li>)}</ul>
              </div>
              <div className="dv-coaches-dialog__actions">
                <Button asChild size="sm" className="dv-coaches-dialog__contact" data-testid="coach-cta-book"><Link to={publicHref("/contact")}>Book a session with {coach.name.split(' ')[0]}</Link></Button>
                <Button asChild size="sm" variant="outline"><Link to={publicHref("/schedule")}>See class schedule</Link></Button>
              </div>
            </div>
          </div>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>}
  </DialogPrimitive.Root>
);

const OurCoachesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState('All');
  const triggerRef = useRef(null);
  const lastSlugRef = useRef(null);
  // The URL is the single source of truth, including browser history and invalid slugs.
  const slug = searchParams.get('coach');
  const openCoach = Object.prototype.hasOwnProperty.call(COACHES_BY_SLUG, slug) ? COACHES_BY_SLUG[slug] : null;
  useEffect(() => { if (openCoach) lastSlugRef.current = openCoach.slug; }, [openCoach]);
  const openCoachWithUrl = (coach, trigger) => {
    triggerRef.current = trigger;
    const next = new URLSearchParams(searchParams);
    next.set('coach', coach.slug);
    setSearchParams(next, { replace: false });
  };
  const closeCoach = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('coach');
    setSearchParams(next, { replace: true });
  };
  const restoreFocus = event => {
    event.preventDefault();
    // Also provide a useful return target for a profile opened directly from a URL.
    const matching = document.querySelector(`[data-testid="coach-card-${lastSlugRef.current}"]`);
    const target = triggerRef.current?.isConnected ? triggerRef.current : matching || document.querySelector('[data-testid="coach-filter-all"]');
    target?.focus({ preventScroll: true });
    if (target && target !== triggerRef.current) target.scrollIntoView({ block: 'center', behavior: 'instant' });
  };
  const filtered = filter === 'All' ? COACHES : COACHES.filter(coach => coach.classes.includes(filter));

  // JSON-LD: each coach as a Person + a CollectionPage wrapper
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Our Coaches — Delta Valley Health Club',
    url: 'https://deltavalleyhealthclub.com/our-coaches',
    mainEntity: COACHES.map((c) => ({
      '@type': 'Person',
      name: c.name,
      jobTitle: c.role,
      worksFor: { '@type': 'HealthClub', name: 'Delta Valley Health Club' },
      knowsAbout: c.classes,
      description: c.philosophy.slice(0, 240),
    })),
  };

  return (
    <main className="dv-coaches" data-testid="our-coaches-page">
      <SEO
        title="Our Coaches & Trainers in Brentwood, CA | Delta Valley Health Club"
        description="Meet the certified personal trainers, group fitness instructors, and MX4 coaches at Delta Valley Health Club in Brentwood. Real bios, real credentials, real people."
        keywords="personal trainers Brentwood CA, MX4 coaches, group fitness instructors, yoga teacher Brentwood, spin instructor, NASM trainer, Delta Valley coaches"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <section className="dv-coaches__hero">
        <div className="dv-coaches__container">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs font-bold tracking-widest uppercase">Meet your team</span>
            <span className="text-xs">· {COACHES.length} certified coaches</span>
          </div>
          <h1 className="dv-size-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 max-w-3xl leading-tight">
            Coaches who know you by name.
          </h1>
          <p className="dv-size-lede text-base md:text-lg text-white/90 max-w-2xl leading-relaxed mb-6">
            Our trainers and instructors live in your community — many for over a decade.
            Personal trainers, MX4 coaches, yoga teachers, spin instructors, and more, all
            certified and ready to help you train smarter.
          </p>
          <div className="flex flex-wrap gap-6 text-white/90">
            <div className="flex items-center gap-2 text-sm">
              <Users size={18} className="text-current" />
              <span><strong className="text-white">{COACHES.length}</strong> certified pros</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin size={18} className="text-current" />
              <span>Brentwood &amp; Discovery Bay</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Award size={18} className="text-current" />
              <span>NASM · ACE · ISSA · MX4 · TRX · Schwinn</span>
            </div>
          </div>
        </div>
      </section>
      {/* The filters stay pinned only while the coach list is on screen. */}
      <div className="dv-coaches__browse">
        <section className="dv-coaches__filters" aria-label="Filter coaches by specialty">
          <div className="dv-coaches__container" data-testid="coach-filter-chips">
            {FILTER_CHIPS.map(c => <button key={c} type="button" aria-pressed={filter === c} onClick={() => setFilter(c)} className="text-xs font-bold" data-testid={`coach-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}>{c}</button>)}
          </div>
        </section>
        <section className="dv-coaches__directory dv-coaches__container" aria-labelledby="coaches-directory-title">
          <h2 id="coaches-directory-title" className="dv-size-heading text-3xl md:text-4xl font-black mb-6">Meet our coaches</h2>
          <div className="dv-coaches__grid">{filtered.map(coach => <CoachCard key={coach.slug} coach={coach} onOpen={openCoachWithUrl} />)}</div>
          <div role="status" aria-live="polite" className={filtered.length ? 'sr-only' : 'dv-coaches__empty'}>
            {filtered.length ? `${filtered.length} coaches shown` : `No coaches teach ${filter} yet.`}
          </div>
        </section>
      </div>
      <TourClosing />
      <CoachModal coach={openCoach} onClose={closeCoach} restoreFocus={restoreFocus} />
    </main>
  );
};

export default OurCoachesPage;
